import React from "react";
import Logo from "assets/new-hire-logo.png";
import { Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  List,
  Menu,
  MenuProps,
  Popover,
  Space,
  Spin,
  Typography,
  message
} from "antd";
import {
  LogoutOutlined,
  UserOutlined,
  CommentOutlined,
  BellFilled,
  MenuOutlined,
  ClockCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import authService from "utils/auth";
import { useQueryClient } from "@tanstack/react-query";
import { storage } from "utils/storage";
// import PATHS from "config/paths";
import { useArchiveNotification } from "api/notifications/useArchiveNotification";
import { useMarkReadNotification } from "api/notifications/useMarkReadNotification";
import { formatDate } from "utils/date";
import { useMarkAllNotificationRead } from "api/notifications/useMarkAllNotification";
import { useIsMobile } from "hooks/useIsMobile";
import useNewMessage from "hooks/useNewMessage";

type MenuItem = Required<MenuProps>["items"][number];

const { Text, Title } = Typography;


const EnumeratorHeader = () => {
  const queryCache = useQueryClient();
  const navigate = useNavigate();

  const user = storage.getUserFromStorage();
  const isMobile = useIsMobile();

  const messageCount = useNewMessage(user?.uid);

  const [notifications] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  const archiveNotificationMutation = useArchiveNotification();
  const markNotificationReadMutation = useMarkReadNotification();
  const markallNotificationMutation = useMarkAllNotificationRead();


  const logout = async () => {
    const logoutMSG: string = await authService.logout(); 
    message.info(logoutMSG); 
    queryCache.clear(); 
    navigate("/"); 
    window.location.reload(); 
  };

  const onClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      logout();
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleMarkAllNotification = () => {
    markallNotificationMutation.mutate();
  };

  const ProfileDropdown = () => {
    return (
      <div className="profileDropdown" onClick={() => navigate(`/enumerator/profile`)}>
        <Avatar
          size="large"
          className="last"
          style={{
            color: "#f56a00",
            backgroundColor: "#fde3cf",
            boxShadow: "rgba(150, 190, 238, 0.35) 0px 0px 6px 1px",
          }}
        >
          {user?.firstName?.[0]}
        </Avatar>
        <div className="profileDropdownInfo">
          <Text strong>
            {user?.firstName} {user?.lastName}
          </Text>
          <Text type="secondary">{user?.email}</Text>
        </div>
      </div>
    );
  };

  const DropdownMenu = ({ text }: any) => {
    return <Text strong>{text}</Text>;
  };

  const items: any = [
    {
      label: <ProfileDropdown />,
      key: "ProfileDropdown",
    },
    {
      type: "divider",
    },
    {
      icon: <UserOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "settingProfile",
      label: (
        <Link to={`/enumerator/profile`}>
          <DropdownMenu text={"Profile"} />
        </Link>
      ),
    },
    {
      icon: <MenuOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "myJobs",
      label: (
        <Link to={"/enumerator/myjobs"} style={{ fontWeight: 600 }}>
          {"My Jobs"}
        </Link>
      ),
    },
    // {
    //   icon: <SettingOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
    //   key: "settingApp",
    //   label: (
    //     <Link to={"/enumerator/settings"} style={{ fontWeight: 600 }}>
    //       {"Setting"}
    //     </Link>
    //   ),
    // },
    {
      icon: <ClockCircleOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "availibilityApp",
      label: (
        <Link to={"/enumerator/availibility"} style={{ fontWeight: 600 }}>
          {"Availability"}
        </Link>
      ),
    },
    {
      type: "divider",
    },
    {
      icon: <LogoutOutlined style={{ fontSize: "16px", paddingRight: "6px" }} />,
      key: "logout",
      label: <Text strong>Logout</Text>,
    },
  ];

  const handleMenuNavigation: MenuProps["onClick"] = (e) => {
    navigate(e?.key);
  };

  const hamburgerMenuItems: MenuItem[] = [
    {
      key: "/enumerator/home",
      label: "Home",
    },
    {
      key: "/enumerator/about",
      label: "About Us",
    },
  ];

  return (
    <>
      <header className=" w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out bg-secondary border-b border-gray-300 ">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center px-5 md:px-0 justify-between h-16 md:h-20">
            <div className="shrink-0 mr-4">
              {isMobile ? (
                <Popover
                  content={
                    <Menu
                      onClick={handleMenuNavigation}
                      className="w-56 px-2"
                      mode="inline"
                      items={hamburgerMenuItems}
                    />
                  }
                  title=""
                  trigger="click"
                >
                  <MenuOutlined className="mr-2 text-2xl font-medium" />
                </Popover>
              ) : (
                <img alt="#" src={Logo} width={280} />
              )}
            </div>
            {!isMobile && (
              <div>
                <ul className="flex grow justify-end flex-wrap items-center gap-x-14">
                  <li>
                    <Link to="/enumerator/home" style={{ color: "#4B5563" }}>
                      Home
                    </Link>
                  </li>
                  <li>
                    <button
                      style={{ color: "#4B5563", cursor: "pointer" }}
                      onClick={() => {
                        navigate(`/enumerator/profile/cv`);
                      }}
                    >
                      Upload CV
                    </button>
                  </li>
                  <li>
                    <Link to="/enumerator/about" style={{ color: "#4B5563" }}>
                      About Us
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            <nav className=" md:flex ">
              <ul className="flex grow justify-end flex-wrap items-center gap-5 md:gap-8">
                <li>
                  <Popover
                    trigger={["click"]}
                    open={open}
                    onOpenChange={handleOpenChange}
                    content={
                      <div className="__notification__container w-80 max-h-500 min-h-56 md:w-96 overflow-auto">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Title level={5}>Notifications</Title>
                          {notifications?.length > 0 && (
                            <Button
                              type="primary"
                              className="h-8 bg-primary text-white border-none"
                              onClick={handleMarkAllNotification}
                              loading={markallNotificationMutation?.isPending}
                            >
                              Mark all Read
                            </Button>
                          )}
                        </div>

                        {markNotificationReadMutation.isPending ||
                        archiveNotificationMutation.isPending ? (
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Spin />
                          </div>
                        ) : (
                          <List
                            itemLayout="horizontal"
                            dataSource={notifications}
                            renderItem={(item: any, index) => (
                              <List.Item
                                style={{
                                  padding: "12px",
                                  borderRadius: "4px",
                                }}
                              >
                                <List.Item.Meta
                                  avatar={
                                    item?.isRead ? (
                                      <Avatar style={{ backgroundColor: "#87d068" }}>
                                        {item?.body[0]}
                                      </Avatar>
                                    ) : (
                                      <Badge dot={true} offset={[1, 8]}>
                                        <Avatar style={{ backgroundColor: "#87d068" }}>
                                          {item?.body[0]}
                                        </Avatar>
                                      </Badge>
                                    )
                                  }
                                  title={<Text strong>{item.subject}</Text>}
                                  description={
                                    <Space direction="vertical">
                                      <Text style={{ fontSize: "13px" }}>{item.body}</Text>
                                      <Text type="secondary" style={{ fontSize: "13px" }}>
                                        <ClockCircleOutlined
                                          style={{
                                            marginRight: "5px",
                                            fontSize: "11px",
                                          }}
                                        />
                                        {formatDate(item?.createdAt)}
                                      </Text>
                                    </Space>
                                  }
                                />
                                <NotificationAction
                                  id={item?._id}
                                  archiveMutation={archiveNotificationMutation}
                                  markMutation={markNotificationReadMutation}
                                  setOpen={setOpen}
                                />
                              </List.Item>
                            )}
                          />
                        )}
                      </div>
                    }
                  >
                    {/* 
                      -.
                    */}
                    <Badge count={notifications.length}>
                      <BellFilled style={{ fontSize: "24px", cursor: "pointer" }} />
                    </Badge>
                  </Popover>
                </li>
                <li>
                  <Badge count={messageCount}>
                    <CommentOutlined
                      style={{ fontSize: "24px", cursor: "pointer" }}
                      onClick={() => navigate("/enumerator/message")}
                    />
                  </Badge>
                </li>

                <Dropdown
                  menu={{
                    items,
                    onClick,
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <li>
                    <UserOutlined style={{ fontSize: "24px", cursor: "pointer" }} />
                  </li>
                </Dropdown>
              </ul>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

type NotificationProps = {
  id: string;
  archiveMutation: any;
  markMutation: any;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const NotificationAction = ({ id, archiveMutation, markMutation, setOpen }: NotificationProps) => {
  const handleDeleteNotification = () => {
    archiveMutation.mutate(id, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  };

  const markNotificationRead = () => {
    markMutation.mutate(id, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  };

  const items = [
    {
      label: <button onClick={handleDeleteNotification}>Archive</button>,
      key: "0",
    },
    {
      label: <button onClick={markNotificationRead}>Mark as read</button>,
      key: "1",
    },
  ];

  return (
    <Dropdown
      menu={{
        items,
        onClick: () => {},
      }}
      trigger={["click"]}
    >
      <SettingOutlined style={{ fontSize: "16px", cursor: "pointer", marginLeft: "22px" }} />
    </Dropdown>
  );
};

export default EnumeratorHeader;
